/**
 * Logo library — every brand's mark in one place, bucketed by what is missing.
 *
 * Previews sit on a checkerboard so transparency is legible: a mark with a baked
 * white box reads very differently from a true transparent PNG, and the whole
 * point of this screen is spotting that before the asset reaches a price sheet.
 */

import React, { useMemo, useState } from 'react';
import { Alert, Box, ButtonBase, CircularProgress } from '@mui/material';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  brandDirectoryApi,
  BrandDirectoryRow,
  LOGO_VARIANT_META,
  LogoAssetVariant,
} from '../../services/brandDirectoryApi';
import {
  bvCardSx,
  bvColor,
  bvContentSx,
  bvPageSx,
  bvRadius,
  bvShadow,
  bvTransition,
  bvType,
} from './brandVendorsTokens';
import { logoQualityOf } from './brandLifecycle';
import { bvRoutes } from './brandVendorsRoutes';
import { tokens } from '../../theme/tokens';
import {
  BvButton,
  BvCard,
  BvEmptyState,
  BvSearchInput,
  IconArrowOut,
  LogoFrame,
  BvPageHeader,
  SegmentedFilter,
  StatusDot,
} from './components/BrandVendorsPrimitives';
import { LogoPreviewBackground, LogoPreviewImage, logoPreviewBackdrop } from './components/LogoPreviewImage';

type LogoFilter = 'all' | 'missing' | 'raster' | 'vector';

/**
 * Light previews use a 12px checkerboard (transparent art reads as transparent
 * rather than white); dark previews use a solid dark ground so a white or pale
 * mark is visible. See `logoPreviewBackdrop`.
 */
const BACKGROUND_OPTIONS: Array<{ value: LogoPreviewBackground; label: string }> = [
  { value: 'light', label: 'Light background' },
  { value: 'dark', label: 'Dark background' },
];

/** Preview filters stand in for per-variant art until each slot has its own file. */
const VARIANT_PREVIEW: Record<LogoAssetVariant, { filter: string; dark: boolean }> = {
  primary: { filter: 'none', dark: false },
  wordmark: { filter: 'none', dark: false },
  monochrome: { filter: 'grayscale(1) contrast(1.4)', dark: false },
  dark: { filter: 'invert(1) grayscale(1) brightness(1.6)', dark: true },
  favicon: { filter: 'none', dark: false },
};

/**
 * A file picker styled as a button. It has to be a `<label>` wrapping the input
 * rather than a `<button>` containing one — an input inside a button is invalid
 * HTML and browsers will not open the picker.
 */
const UploadButton: React.FC<{
  label: string;
  ariaLabel: string;
  disabled?: boolean;
  onFile: (file: File) => void;
}> = ({ label, ariaLabel, disabled = false, onFile }) => (
  <Box
    component="label"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 26,
      px: '9px',
      borderRadius: `${bvRadius.xsmall}px`,
      bgcolor: bvColor.surface,
      border: `1px solid ${bvColor.border}`,
      color: disabled ? bvColor.muted : bvColor.ink3,
      cursor: disabled ? 'not-allowed' : 'pointer',
      whiteSpace: 'nowrap',
      transition: bvTransition,
      ...bvType.buttonSmall,
      '&:hover': disabled ? {} : { borderColor: bvColor.accent, color: bvColor.accentHover },
      '&:focus-within': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
    }}
  >
    {label}
    <Box
      component="input"
      type="file"
      aria-label={ariaLabel}
      disabled={disabled}
      accept="image/png,image/svg+xml,image/webp"
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) onFile(file);
        event.target.value = '';
      }}
      sx={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
    />
  </Box>
);

/**
 * Three states with three different tokens and a text mark each, so missing and
 * raster-only never rely on a red-vs-brown colour difference alone.
 */
const statusOf = (row: BrandDirectoryRow) => {
  const quality = logoQualityOf(row);
  if (quality === 'none') {
    return { dot: tokens.signal.problem.solid, mark: '×', note: 'Missing — monogram shown', tone: tokens.signal.problem.ink };
  }
  if (quality === 'raster') {
    return { dot: tokens.signal.info.solid, mark: '◐', note: 'Raster only — no vector', tone: tokens.signal.info.ink };
  }
  return { dot: bvColor.success, mark: '✓', note: 'Vector on file', tone: bvColor.success };
};

export const BrandVendorsLogoLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<LogoFilter>('all');
  const [previewBg, setPreviewBg] = useState<LogoPreviewBackground>('light');
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const selectedId = params.get('brand');

  const { data, isLoading, error } = useQuery({
    queryKey: ['brand-directory'],
    queryFn: () => brandDirectoryApi.list(),
  });

  const brands = useMemo(() => data?.brands || [], [data]);
  const selected = brands.find((row) => row.id === selectedId) || null;

  const buckets = useMemo(
    () => ({
      all: brands.length,
      missing: brands.filter((row) => row.missingLogo).length,
      raster: brands.filter((row) => logoQualityOf(row) === 'raster').length,
      vector: brands.filter((row) => logoQualityOf(row) === 'svg').length,
    }),
    [brands]
  );

  const filtered = useMemo(
    () =>
      brands.filter((row) => {
        if (filter === 'missing' && !row.missingLogo) return false;
        if (filter === 'raster' && logoQualityOf(row) !== 'raster') return false;
        if (filter === 'vector' && logoQualityOf(row) !== 'svg') return false;
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return [row.name, row.slug, ...(row.aliases || [])].join(' ').toLowerCase().includes(q);
      }),
    [brands, filter, search]
  );

  const saveAsset = useMutation({
    mutationFn: ({ variant, url, format }: { variant: LogoAssetVariant; url: string; format: 'raster' | 'vector' }) =>
      brandDirectoryApi.updateLogoAsset(selected!.id, variant, { url, format }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['brand-directory'] }),
  });

  const handleUpload = async (variant: LogoAssetVariant, file: File) => {
    if (!selected || !currentUser) return;
    const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
    const extension = isSvg ? 'svg' : 'png';
    const filePath = `images/brand-logos/${selected.id}/${variant}-${Date.now()}.${extension}`;
    setUploading(variant);
    setUploadError(null);
    try {
      const fileRef = storageRef(storage, filePath);
      await uploadBytes(fileRef, file, { contentType: isSvg ? 'image/svg+xml' : file.type || 'image/png' });
      const url = await getDownloadURL(fileRef);
      await saveAsset.mutateAsync({ variant, url, format: isSvg ? 'vector' : 'raster' });
    } catch (uploadFailure) {
      setUploadError((uploadFailure as Error).message);
    } finally {
      setUploading(null);
    }
  };

  const filledCount = selected
    ? (Object.keys(LOGO_VARIANT_META) as LogoAssetVariant[]).filter((key) => selected.logoAssets?.[key]?.url).length
    : 0;

  return (
    <Box sx={bvPageSx}>
      <Box sx={{ ...bvContentSx, display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <BvPageHeader
          title="Brand logos"
          description="One mark per brand is the minimum — product cards, storefronts and printed price sheets all fall back to a monogram without it."
          actions={
            <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <SegmentedFilter<LogoPreviewBackground>
                ariaLabel="Logo preview background"
                value={previewBg}
                onChange={setPreviewBg}
                options={BACKGROUND_OPTIONS}
              />
              <SegmentedFilter<LogoFilter>
                ariaLabel="Filter brands by logo coverage"
                value={filter}
                onChange={setFilter}
                options={[
                  { value: 'all', label: 'All', count: buckets.all },
                  { value: 'missing', label: 'Missing', count: buckets.missing },
                  { value: 'raster', label: 'Raster only', count: buckets.raster },
                  { value: 'vector', label: 'Vector', count: buckets.vector },
                ]}
              />
            </Box>
          }
        />

        {error && <Alert severity="error">{(error as Error).message}</Alert>}
        {uploadError && <Alert severity="error" onClose={() => setUploadError(null)}>{uploadError}</Alert>}

        {isLoading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: '80px' }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(300px, 1fr) minmax(300px, 400px)' },
              gap: '16px',
              alignItems: 'start',
            }}
          >
            {/* tile grid */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 }}>
            <BvSearchInput
              ariaLabel="Search brand logos"
              value={search}
              onChange={setSearch}
              placeholder="Search brands"
              width={320}
            />
            {filtered.length === 0 ? (
              <Box sx={bvCardSx}>
                <BvEmptyState headline={search.trim() ? 'No brands match that search.' : 'No brands in this view.'} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(auto-fill, minmax(132px, 1fr))',
                    md: 'repeat(auto-fill, minmax(132px, 1fr))',
                  },
                  gap: '12px',
                }}
              >
                {filtered.map((row) => {
                  const status = statusOf(row);
                  const isSelected = row.id === selectedId;
                  return (
                    <ButtonBase
                      key={row.id}
                      aria-pressed={isSelected}
                      onClick={() => setParams({ brand: row.id }, { replace: true })}
                      sx={{
                        display: 'block',
                        textAlign: 'left',
                        bgcolor: bvColor.surface,
                        border: `1px solid ${isSelected ? bvColor.accent : bvColor.border}`,
                        borderRadius: `${bvRadius.control}px`,
                        overflow: 'hidden',
                        boxShadow: isSelected ? bvShadow.card : 'none',
                        transition: bvTransition,
                        '&:hover': { borderColor: bvColor.accent },
                        '&:focus-visible': { outline: `2px solid ${bvColor.accent}`, outlineOffset: 1 },
                      }}
                    >
                      <Box sx={{ height: 84, display: 'grid', placeItems: 'center', ...logoPreviewBackdrop(previewBg) }}>
                        {row.logoUrl ? (
                          <LogoPreviewImage src={row.logoUrl} maxWidth={92} maxHeight={50} />
                        ) : (
                          <Box
                            aria-hidden
                            sx={{
                              width: 42,
                              height: 42,
                              display: 'grid',
                              placeItems: 'center',
                              border: `1px dashed ${bvColor.border}`,
                              borderRadius: `${bvRadius.small}px`,
                              bgcolor: bvColor.surface,
                              color: bvColor.muted2,
                              fontSize: 16,
                              fontWeight: 600,
                            }}
                          >
                            {row.name.charAt(0).toUpperCase()}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ px: '9px', py: '8px', borderTop: `1px solid ${bvColor.borderSubtle}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <StatusDot color={status.dot} size={7} />
                          <Box
                            sx={{
                              ...bvType.tableSecondary,
                              fontWeight: 500,
                              color: bvColor.ink,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.name}
                          </Box>
                        </Box>
                        <Box sx={{ ...bvType.microCaption, color: status.tone, mt: '2px' }}>
                          <Box component="span" aria-hidden sx={{ mr: '4px', fontWeight: 700 }}>{status.mark}</Box>
                          {status.note}
                        </Box>
                      </Box>
                    </ButtonBase>
                  );
                })}
              </Box>
            )}
            </Box>

            {/* detail panel (sticky so it stays in view while scrolling the grid) */}
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 }, alignSelf: 'start', maxHeight: { lg: 'calc(100vh - 32px)' }, overflowY: { lg: 'auto' } }}>
            <BvCard>
              {selected ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', p: '13px', borderBottom: `1px solid ${bvColor.borderSubtle}` }}>
                    <LogoFrame name={selected.name} url={selected.logoUrl} size={44} artSize={34} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ fontSize: 15.5, fontWeight: 600, color: bvColor.ink }}>{selected.name}</Box>
                      <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '2px' }}>
                        {statusOf(selected).note} · {filledCount} of 5 variants
                      </Box>
                    </Box>
                    <BvButton
                      size="sm"
                      endIcon={<IconArrowOut size={12} />}
                      onClick={() => navigate(bvRoutes.brand(selected.id))}
                    >
                      Brand
                    </BvButton>
                  </Box>

                  <Box sx={{ p: '13px', display: 'flex', flexDirection: 'column', gap: '9px' }}>
                    {(Object.keys(LOGO_VARIANT_META) as LogoAssetVariant[]).map((variant) => {
                      const asset = selected.logoAssets?.[variant];
                      const preview = VARIANT_PREVIEW[variant];
                      const filled = Boolean(asset?.url);
                      // Until each slot has its own upload, an unfilled slot previews
                      // the primary mark through that variant's filter.
                      const previewUrl = asset?.url || selected.logoUrl || null;
                      return (
                        <Box
                          key={variant}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '11px',
                            p: '9px',
                            border: `1px ${filled ? 'solid' : 'dashed'} ${filled ? bvColor.borderSubtle : bvColor.border}`,
                            borderRadius: `${bvRadius.control}px`,
                            bgcolor: filled ? bvColor.surface : 'transparent',
                          }}
                        >
                          <Box
                            sx={{
                              width: 62,
                              height: 40,
                              flexShrink: 0,
                              display: 'grid',
                              placeItems: 'center',
                              borderRadius: `${bvRadius.chip}px`,
                              border: `1px solid ${bvColor.borderSubtle}`,
                              bgcolor: preview.dark ? bvColor.ink : bvColor.page,
                            }}
                          >
                            {previewUrl ? (
                              <LogoPreviewImage
                                src={previewUrl}
                                maxWidth={48}
                                maxHeight={28}
                                filter={preview.filter}
                                opacity={filled ? 1 : 0.45}
                                compact
                              />
                            ) : (
                              <Box aria-hidden sx={{ color: bvColor.muted2, fontSize: 15 }}>
                                +
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ ...bvType.tableSecondary, fontWeight: 500, color: bvColor.ink }}>
                              {LOGO_VARIANT_META[variant].label}
                            </Box>
                            <Box sx={{ ...bvType.microCaption, color: bvColor.muted, mt: '2px' }}>
                              {filled
                                ? `${asset?.format === 'vector' ? 'SVG' : 'Raster'} on file`
                                : LOGO_VARIANT_META[variant].hint}
                            </Box>
                          </Box>
                          <UploadButton
                            label={uploading === variant ? 'Uploading…' : filled ? 'Replace' : 'Upload'}
                            ariaLabel={`${filled ? 'Replace' : 'Upload'} ${LOGO_VARIANT_META[variant].label} for ${selected.name}`}
                            disabled={uploading !== null}
                            onFile={(file) => void handleUpload(variant, file)}
                          />
                        </Box>
                      );
                    })}
                  </Box>

                  <Box
                    sx={{
                      px: '13px',
                      pb: '13px',
                      ...bvType.caption,
                      color: bvColor.muted,
                      lineHeight: 1.5,
                    }}
                  >
                    Each slot is uploaded on its own. Replacing the primary mark also updates the logo shown across the
                    app; the other slots are used only where their variant is called for.
                  </Box>
                </>
              ) : (
                <BvEmptyState
                  headline="Pick a brand"
                  note="Select a tile to see its five variant slots and upload the ones that are missing."
                />
              )}
            </BvCard>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default BrandVendorsLogoLibraryPage;
